import { Drug, DrugSearchResult } from '../common/models/types';

/**
 * 약품 이미지 S3 경로 상수
 * TODO: 환경변수로 이동 고려
 */
export const DRUG_IMAGE_BASE_URL =
  'https://s3.ap-northeast-2.amazonaws.com/storage.dev.ymyd.onjourney.co.kr/phai/drugs/pictures';

/**
 * itemCode로 약품 이미지 URL 생성
 *
 * @param itemCode - 약품 품목코드
 * @returns S3 약품 이미지 전체 URL
 *
 * @example
 * getDrugImageUrl('200001234')
 * // => 'https://s3.../phai/drugs/pictures/200001234.jpg'
 */
export const getDrugImageUrl = (itemCode: string): string => {
  return `${DRUG_IMAGE_BASE_URL}/${itemCode}.jpg`;
};

/**
 * drugsData에서 itemCode로 제품명 조회
 *
 * @param itemCode - 약품 품목코드
 * @param drugsData - 약품 정보 배열 (선택)
 * @returns 제품명 문자열, 없으면 빈 문자열
 *
 * @example
 * getDrugProductName('200001234', drugsData)
 * // => '타이레놀정 500mg'
 */
export const getDrugProductName = (
  itemCode: string,
  drugsData?: Drug[]
): string => {
  return drugsData?.find(d => d.itemCode === itemCode)?.productName || '';
};

/**
 * ⑆img_ 패턴에서 itemCode 추출
 *
 * @param pattern - ⑆img_itemcode 형식의 문자열
 * @returns itemCode 문자열
 *
 * @example
 * extractItemCode('⑆img_200001234')
 * // => '200001234'
 */
export const extractItemCode = (pattern: string): string => {
  return pattern.replace('⑆img_', '');
};

/**
 * 약품 이미지 패턴 정규표현식
 * ⑆img_[itemCode] 형식 매칭
 */
export const DRUG_IMAGE_PATTERN = /⑆img_([A-Za-z0-9]+)/g;

/**
 * 허가사항 이미지 S3 경로 상수
 */
export const DRUG_LABEL_IMAGE_BASE_URL =
  'https://s3.ap-northeast-2.amazonaws.com/storage.dev.ymyd.onjourney.co.kr/phai/drugs/labels';

/**
 * 허가사항 이미지 플레이스홀더 패턴
 * {{IMG:001}}, {{IMG:002}} 등 매칭
 */
export const LABEL_IMAGE_PLACEHOLDER_PATTERN = /\{\{IMG:(\d{3})\}\}/g;

/**
 * 허가사항 이미지 S3 URL 생성
 * @param itemCode 품목코드
 * @param serial 3자리 시리얼 번호 (001, 002, ...)
 * @returns S3 이미지 URL
 *
 * @example
 * getLabelImageUrl('202401812', '001')
 * // => 'https://s3.../phai/drugs/labels/202401812_001'
 */
export const getLabelImageUrl = (itemCode: string, serial: string): string => {
  return `${DRUG_LABEL_IMAGE_BASE_URL}/${itemCode}_${serial}`;
};

/**
 * pillsData에서 itemCode로 성상(CHART) 조회
 *
 * @param itemCode - 약품 품목코드
 * @param pillsData - 약품 식별정보 배열 (선택)
 * @returns 성상 문자열 (예: "녹색의 원형 필름코팅정"), 없으면 빈 문자열
 *
 * @example
 * getDrugChart('200001234', pillsData)
 * // => '녹색의 원형 필름코팅정'
 */
export const getDrugChart = (
  itemCode: string,
  pillsData?: DrugSearchResult[]
): string => {
  return pillsData?.find(p => p.itemSeq === itemCode)?.chart || '';
};

/**
 * pillsData에서 itemCode로 각인(PRINT) 조회
 * 앞면과 뒷면 각인을 결합하여 반환
 *
 * @param itemCode - 약품 품목코드
 * @param pillsData - 약품 식별정보 배열 (선택)
 * @returns 각인 문자열 (예: "YH / LT"), 없으면 빈 문자열
 *
 * @example
 * getDrugPrint('200001234', pillsData)
 * // => 'YH / LT'
 */
export const getDrugPrint = (
  itemCode: string,
  pillsData?: DrugSearchResult[]
): string => {
  const pill = pillsData?.find(p => p.itemSeq === itemCode);
  if (!pill) return '';
  const prints = [pill.printFront, pill.printBack].filter(Boolean);
  return prints.join(' / ');
};

/**
 * patientSnap에서 본문 약 카드 이름 소스 `{ itemCode, productName }`를 수집한다(D-18 · P-3).
 *
 * 파트너 화면(탑재뷰·독립뷰)은 성상(pills)이 처방 외 OTC·건기식·식별의약품 itemCode까지 덮으므로,
 * 이름 소스를 그 범위로 넓히지 않으면 "성상만 있고 이름 없는 카드"가 생긴다.
 *
 * ⚠️ `patientSnap`은 `any`다(백엔드 미배포 전이 — 부재·부분·비배열 가능). 모든 접근을 `Array.isArray`로
 *    감싼다. **`itemCode`와 `productName`이 모두 있는 항목만 방출한다(P-5)** — `getDrugProductName`은
 *    `find` 첫 매칭에서 멈추므로, 이름 없는 상위 항목이 있으면 하위 `pillsNameFallback`의 실이름을 가린다.
 * ⚠️ 식별의약품(`DrugIdentification`)엔 이름 필드가 없다 — `selectedItemCode`로 `results`(DrugSearchResult[])를
 *    조인해 `itemName`을 이름으로 쓴다. 조인 실패(이름 없음)면 방출하지 않는다.
 */
export const collectSnapDrugs = (patientSnap: any): Drug[] => {
  if (!patientSnap || typeof patientSnap !== 'object') return [];
  const out: Drug[] = [];
  const pushRx = (rx: any) => {
    const itemCode = rx?.itemCode;
    const productName = rx?.medicationName;
    if (itemCode && productName) out.push({ itemCode, productName });
  };
  // 처방 그룹의 처방약
  if (Array.isArray(patientSnap.prescriptionGroups)) {
    patientSnap.prescriptionGroups.forEach((g: any) => {
      if (Array.isArray(g?.prescriptions)) g.prescriptions.forEach(pushRx);
    });
  }
  // 최상위 처방/OTC/건기식(전부 Prescription 형태)
  (['prescriptions', 'otc', 'hff'] as const).forEach((key) => {
    if (Array.isArray(patientSnap[key])) patientSnap[key].forEach(pushRx);
  });
  // 식별의약품 — selectedItemCode로 results를 조인해 이름을 얻는다
  if (Array.isArray(patientSnap.drugIdentifications)) {
    patientSnap.drugIdentifications.forEach((di: any) => {
      const itemCode = di?.selectedItemCode;
      if (!itemCode) return;
      const productName = Array.isArray(di?.results)
        ? di.results.find((r: any) => r?.itemSeq === itemCode)?.itemName
        : undefined;
      if (productName) out.push({ itemCode, productName });
    });
  }
  return out;
};

/**
 * pills(성상 배열)를 이름 최저 폴백 소스로 매핑한다(D-18 · P-4). `itemSeq → itemCode`, `itemName → productName`.
 *
 * `pills`와 `patientSnap`은 다른 백엔드 세션이 배포하므로 부분/역순 도착이 가능하다. `pills`만 와도
 * 성상 있는 카드에 최소한 품목명이 붙게 한다. 우선순위가 가장 낮아(`collectSnapDrugs` 뒤에 붙임)
 * 처방명이 있으면 처방명이 이긴다. **`itemName` 없는 pill은 방출하지 않는다(P-5)** — 이름 없는 항목이
 * `getDrugProductName`의 find를 선점해 빈 값을 돌려주는 것을 막는다.
 *
 * ⚠️ `itemSeq`·`itemName`만 읽는다 — `itemImage`(nedrug URL)는 읽지 않는다(D-13 · 외부 이미지 로드 금지).
 */
export const pillsNameFallback = (pills?: DrugSearchResult[]): Drug[] => {
  if (!Array.isArray(pills)) return [];
  return pills
    .filter(p => p?.itemSeq && p?.itemName)
    .map(p => ({ itemCode: p.itemSeq, productName: p.itemName }));
};
